/**
 * Marketing email preferences (Customer.io subscription topics) for Settings.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card'
import { Switch } from './ui/switch'
import { Label } from './ui/label'
import { Mail } from 'lucide-react'
import { apiService } from '../services/api'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

const MANAGED_TOPIC_IDS = [1, 2, 3, 4, 5, 6, 8] as const

export interface EmailPreferencesProps {
  className?: string
  compact?: boolean
}

type TopicsState = Record<string, boolean>

export interface MarketingSubscriptionsApiResponse {
  topics: TopicsState
  channels: TopicsState
  cio_person_id_strategy: string
  managed_topic_ids: number[]
  customer_io_sync_ok?: boolean
  customer_io_sync_error?: string | null
}

export function EmailPreferences({
  className = '',
}: EmailPreferencesProps) {
  const { t } = useTranslation('settings')
  const [topics, setTopics] = useState<TopicsState>({})
  const [emailChannel, setEmailChannel] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const topicMeta = useMemo(
    () =>
      MANAGED_TOPIC_IDS.map((id) => ({
        id,
        titleKey: `marketingTopics.${id}.title`,
        descKey: `marketingTopics.${id}.description`,
      })),
    []
  )

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await apiService.get<MarketingSubscriptionsApiResponse>(
        '/user/marketing-subscriptions'
      )
      setTopics(data.topics || {})
      setEmailChannel(Boolean(data.channels?.email ?? true))
    } catch (e) {
      console.error(e)
      toast.error(t('marketingTopics.loadFailed') as string)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const persist = async (nextTopics: TopicsState, nextEmail: boolean) => {
    try {
      setSaving(true)
      const data = await apiService.put<MarketingSubscriptionsApiResponse>(
        '/user/marketing-subscriptions',
        {
          topics: nextTopics,
          channels: { email: nextEmail },
        }
      )
      setTopics(data.topics)
      setEmailChannel(Boolean(data.channels?.email ?? true))
      if (data.customer_io_sync_ok === false && data.customer_io_sync_error) {
        toast.warning(
          (t('marketingTopics.savedWithSyncWarning') as string) +
            ` (${data.customer_io_sync_error})`
        )
      } else {
        toast.success(t('marketingTopics.saved') as string)
      }
    } catch (e) {
      console.error(e)
      toast.error(t('marketingTopics.saveFailed') as string)
    } finally {
      setSaving(false)
    }
  }

  const toggleTopic = async (topicId: number) => {
    const key = String(topicId)
    const next = { ...topics, [key]: !topics[key] }
    setTopics(next)
    await persist(next, emailChannel)
  }

  const toggleEmailChannel = async () => {
    const next = !emailChannel
    setEmailChannel(next)
    await persist(topics, next)
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-2 bg-muted rounded w-full" />
            <div className="h-2 bg-muted rounded w-2/3" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="h-5 w-5" />
          {t('marketingTopics.cardTitle')}
        </CardTitle>
        <CardDescription>{t('marketingTopics.cardDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
          <div>
            <Label htmlFor="marketing-email-channel" className="font-medium text-sm">
              {t('marketingTopics.emailChannelTitle')}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {t('marketingTopics.emailChannelDescription')}
            </p>
          </div>
          <Switch
            id="marketing-email-channel"
            checked={emailChannel}
            onCheckedChange={() => void toggleEmailChannel()}
            disabled={saving}
          />
        </div>

        <div className="space-y-4">
          {topicMeta.map(({ id, titleKey, descKey }) => (
            <div
              key={id}
              className="flex items-start justify-between gap-4 rounded-lg border p-3"
            >
              <div className="flex-1 min-w-0">
                <Label htmlFor={`topic-${id}`} className="font-medium text-sm cursor-pointer">
                  {t(titleKey)}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">{t(descKey)}</p>
              </div>
              <Switch
                id={`topic-${id}`}
                checked={Boolean(topics[String(id)] ?? true)}
                onCheckedChange={() => void toggleTopic(id)}
                disabled={saving || !emailChannel}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
